import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { MessageCircle, Repeat2, Heart, Image as ImageIcon, PlayCircle, ExternalLink } from 'lucide-react';
import { Tweet } from '@/types/twitter';
import { Tag } from '@/types/tags';

interface CompactTweetCardProps {
    tweet: Tweet;
    profileImage?: string;
    linkedTags?: Tag[];
    hideEntityTags?: boolean;
    onEntityClick?: (entityId: string, entitySlug: string) => void; // Callback for entity badge click
    /** Base path for entity social links. The chain dashboard passes its scoped
        `/<chain>/social` so cards stay in the chain lens; the default targets the
        primary chain's social surface (the bare `/social` index is retired).
        TODO(multichain): derive the default from the active chain when chain #2 lands. */
    entitySocialBase?: string;
    className?: string;
}

export function CompactTweetCard({ tweet, profileImage, linkedTags, hideEntityTags = false, onEntityClick, entitySocialBase = '/abstract/social', className = '' }: CompactTweetCardProps) {
    const router = useRouter();
    const [isExpanded, setIsExpanded] = useState(false);

    // Resolved entity (first linked tag) + its scoped social link.
    const entityId = linkedTags && linkedTags.length > 0 ? linkedTags[0].id : null;
    const entitySlug = linkedTags && linkedTags.length > 0 ? linkedTags[0].slug : null;
    const entityHref = entitySlug ? `${entitySocialBase}?entity=${entitySlug}` : '#';

    // Plain left-click on an entity link → focus-in-place when the host opts in; modified
    // clicks (new tab etc.) fall through to the real href so the link stays crawlable.
    const onEntityLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (!onEntityClick || !entityId || !entitySlug) return;
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        e.stopPropagation();
        onEntityClick(entityId, entitySlug);
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const formatNumber = (num: number) => {
        if (!num) return '0';
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return num.toString();
    };

    const hasPhoto = tweet.media?.some(m => m.type === 'photo');
    const hasVideo = tweet.media?.some(m => m.type === 'video' || m.type === 'gif');

    // Get entity type label
    const entityTypeLabel = linkedTags && linkedTags.length > 0 ? linkedTags[0].entityType : null;
    const entityTypeLabelMap: Record<string, string> = {
        'person': 'Person',
        'project': 'Project',
        'protocol': 'Protocol',
        'company': 'Company',
        'dao': 'DAO',
        'nft-collection': 'NFT Collection',
        'token': 'Token'
    };

    const cleanTweetText = (text: string) => {
        // Remove t.co links that appear at the end of the tweet (usually media/quote links)
        return text.replace(/\s*https:\/\/t\.co\/\w+\s*$/, '');
    };

    // Handle card click - navigate to entity social page
    const handleCardClick = (e: React.MouseEvent) => {
        // Don't navigate if clicking on interactive elements
        const target = e.target as HTMLElement;
        if (target.closest('a') || target.closest('button')) {
            return;
        }
        
        // Focus-in-place when the host opts in (chain dashboard); else navigate to the
        // (scoped) social entity view.
        if (entityId && entitySlug) {
            if (onEntityClick) onEntityClick(entityId, entitySlug);
            else router.push(entityHref);
        }
    };

    // Twitter intent URLs for engagement
    const replyUrl = `https://twitter.com/intent/tweet?in_reply_to=${tweet.id}`;
    const retweetUrl = `https://twitter.com/intent/retweet?tweet_id=${tweet.id}`;
    const likeUrl = `https://twitter.com/intent/like?tweet_id=${tweet.id}`;

    return (
        <div 
            className={`bg-white dark:bg-brand-dark-lighter rounded-xl p-4 border border-gray-200 dark:border-neutral-800 hover:border-blue-500/30 dark:hover:border-blue-500/30 transition-all duration-200 group ${linkedTags && linkedTags.length > 0 ? 'cursor-pointer' : ''} ${className}`}
            onClick={handleCardClick}
        >
            {/* Retweet Indicator */}
            {tweet.isRetweet && (
                <div className="flex items-center gap-1.5 mb-2 text-xs text-gray-500 dark:text-gray-400">
                    <Repeat2 className="w-3.5 h-3.5" />
                    <span>{tweet.username} retweeted</span>
                </div>
            )}

            {/* Reply Context Indicator. A self-reply (author replying to itself) is a THREAD
                continuation, not a reply to someone else — label it as such so it doesn't read
                as a disconnected tweet. (True nested threading needs the parent tweet id, which
                we don't capture for these replies yet.) */}
            {tweet.isReply && tweet.inReplyToUsername && (
                tweet.inReplyToUsername.toLowerCase() === (tweet.username || '').toLowerCase() ? (
                    <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-scout-gold">
                        <MessageCircle className="w-3.5 h-3.5" />
                        <span>🧵 Thread — {tweet.username} continued</span>
                    </div>
                ) : (
                    <div className="flex items-center gap-1.5 mb-2 text-xs text-gray-500 dark:text-gray-400">
                        <MessageCircle className="w-3.5 h-3.5" />
                        <span>Replying to <span className="text-blue-500">@{tweet.inReplyToUsername}</span></span>
                    </div>
                )
            )}

            {/* Header Row */}
            <div className="flex items-start justify-between mb-3">
                {/* Left: Profile Image + Name */}
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    {/* Profile Image - Click to go to social page */}
                    {linkedTags && linkedTags.length > 0 ? (
                        <Link
                            href={entityHref}
                            onClick={onEntityLinkClick}
                            className="relative w-9 h-9 flex-shrink-0 rounded-full ring-2 ring-transparent hover:ring-scout-gold/50 transition-all"
                        >
                            {profileImage ? (
                                <Image
                                    src={profileImage}
                                    alt={tweet.username}
                                    fill
                                    className="rounded-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                    {tweet.username.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </Link>
                    ) : (
                        <div className="relative w-9 h-9 flex-shrink-0">
                            {profileImage ? (
                                <Image
                                    src={profileImage}
                                    alt={tweet.username}
                                    fill
                                    className="rounded-full object-cover"
                                />
                            ) : (
                                <div className="w-9 h-9 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                    {tweet.username.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                            {linkedTags && linkedTags.length > 0 ? (
                                <Link
                                    href={entityHref}
                                    onClick={onEntityLinkClick}
                                    className="font-semibold text-gray-900 dark:text-white text-sm truncate hover:text-scout-gold transition-colors"
                                    style={{ maxWidth: 'min(180px, 50%)' }}
                                >
                                    {tweet.username}
                                </Link>
                            ) : (
                                <span 
                                    className="font-semibold text-gray-900 dark:text-white text-sm truncate"
                                    style={{ maxWidth: 'min(180px, 50%)' }}
                                >
                                    {tweet.username}
                                </span>
                            )}
                            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
                                • {formatDate(tweet.createdAt)}
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5 min-w-0">
                            <a
                                href={`https://twitter.com/${tweet.handleId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-gray-500 dark:text-gray-400 truncate hover:text-blue-500 transition-colors"
                                title={`View @${tweet.handleId} on X`}
                            >
                                @{tweet.handleId}
                            </a>
                            {/* Entity Badge - shown on all screen sizes when there's room */}
                            {!hideEntityTags && linkedTags && linkedTags.length > 0 && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onEntityClick) onEntityClick(linkedTags[0].id, linkedTags[0].slug);
                                    }}
                                    className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-scout-gold/10 text-scout-gold hover:bg-scout-gold/20 transition-colors truncate flex-shrink-0"
                                    style={{ maxWidth: 'min(120px, 40%)' }}
                                >
                                    {linkedTags[0].name}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Retweeted Tweet Content (if this is a retweet) */}
            {tweet.isRetweet && tweet.retweetedTweet && (
                <div className="mb-3 border-l-2 border-blue-500 pl-3">
                    <div className="flex items-center gap-2 mb-2">
                        {tweet.retweetedTweet.authorProfilePic && (
                            <div className="relative w-5 h-5 flex-shrink-0">
                                <Image
                                    src={tweet.retweetedTweet.authorProfilePic}
                                    alt={tweet.retweetedTweet.username}
                                    fill
                                    className="rounded-full object-cover"
                                />
                            </div>
                        )}
                        <span className="font-semibold text-sm text-gray-900 dark:text-white">
                            {tweet.retweetedTweet.displayName || tweet.retweetedTweet.username}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            @{tweet.retweetedTweet.username}
                        </span>
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                        {cleanTweetText(tweet.retweetedTweet.text)}
                    </div>
                </div>
            )}

            {/* Tweet Text (plain tweets — and retweets whose original-tweet payload is absent:
                the current scraper payload sets isRetweet but never retweetedTweet, so without
                this fallback a retweet renders as a contentless husk; the stored "RT @…" text is
                exactly what the global InterceptCard shows) */}
            {(!tweet.isRetweet || !tweet.retweetedTweet) && !tweet.article && (
                <div className="text-sm text-gray-700 dark:text-gray-300 mb-3 whitespace-pre-wrap leading-relaxed">
                    {cleanTweetText(tweet.text)}
                </div>
            )}

            {/* X Article (long-form): cover + label + title + preview + link out — a clear
                "this is an article" card, not a bare t.co link. We tease + push to X for the
                verbatim original; our own coverage is what we publish. */}
            {!tweet.isRetweet && tweet.article && (
                <a
                    href={tweet.article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="block mb-3 rounded-lg border border-gray-200 dark:border-neutral-700 overflow-hidden hover:border-blue-500/40 transition-colors"
                >
                    {tweet.article.coverImage && (
                        // eslint-disable-next-line @next/next/no-img-element -- raw pbs.twimg.com cover, not in next/image domains
                        <img
                            src={tweet.article.coverImage}
                            alt={tweet.article.title || 'Article cover'}
                            loading="lazy"
                            className="w-full h-40 object-cover"
                        />
                    )}
                    <div className="p-3">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-scout-gold">📄 Article</span>
                        <div className="font-semibold text-gray-900 dark:text-white text-sm mt-0.5">{tweet.article.title}</div>
                        {tweet.article.previewText && (
                            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-3 leading-relaxed">
                                {tweet.article.previewText}
                            </div>
                        )}
                        <span className="inline-flex items-center gap-1 text-xs text-blue-500 mt-1.5">
                            Read full article on X <ExternalLink className="w-3 h-3" />
                        </span>
                    </div>
                </a>
            )}

            {/* Quoted Tweet */}
            {tweet.quotedTweet && (
                <div className="mt-3 mb-3 border-l-4 border-blue-400 dark:border-blue-500 bg-blue-50/50 dark:bg-blue-900/10 rounded-r-xl p-3 hover:bg-blue-100/50 dark:hover:bg-blue-900/20 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">📎</span>
                        <div className="relative w-5 h-5 flex-shrink-0">
                            {tweet.quotedTweet.authorProfilePic ? (
                                <Image
                                    src={tweet.quotedTweet.authorProfilePic}
                                    alt={tweet.quotedTweet.username}
                                    fill
                                    className="rounded-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full bg-gray-200 dark:bg-neutral-700 rounded-full" />
                            )}
                        </div>
                        <span className="font-semibold text-xs text-gray-900 dark:text-white truncate">
                            @{tweet.quotedTweet.username}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            • {formatDate(tweet.quotedTweet.createdAt)}
                        </span>
                    </div>
                    <div className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                        {cleanTweetText(tweet.quotedTweet.text)}
                    </div>
                    {tweet.quotedTweet.media && tweet.quotedTweet.media.length > 0 && (
                        <div className="rounded-lg overflow-hidden relative w-full bg-gray-100 dark:bg-neutral-800 mt-2">
                            <img
                                src={tweet.quotedTweet.media[0].url}
                                alt="Quoted tweet media"
                                className="w-full h-auto object-contain max-h-[400px]"
                                loading="lazy"
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Media Content (Always Visible). Skip when this is an X Article whose cover we
                already render inside the Article card above — the cover is also promoted into
                `media` at ingestion, so showing both would duplicate the image. */}
            {tweet.media && tweet.media.length > 0 && !(tweet.article && tweet.article.coverImage) && (
                <div className="mb-3 rounded-lg overflow-hidden relative bg-gray-100 dark:bg-neutral-800">
                    {tweet.media[0].type === 'photo' ? (
                        <div className="relative w-full">
                            <img
                                src={tweet.media[0].url}
                                alt="Tweet media"
                                className="w-full h-auto object-contain max-h-[600px]"
                                loading="lazy"
                            />
                        </div>
                    ) : tweet.media[0].type === 'gif' ? (
                        <div className="relative w-full bg-black rounded-lg overflow-hidden">
                            <video
                                src={tweet.media[0].url}
                                poster={tweet.media[0].thumbnailUrl}
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-auto object-contain max-h-[600px]"
                            />
                            <div className="absolute bottom-2 left-2 bg-black/70 text-white text-[10px] font-bold px-1.5 py-0.5 rounded backdrop-blur-sm pointer-events-none">
                                GIF
                            </div>
                        </div>
                    ) : (
                        <a
                            href={tweet.tweetUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="relative w-full aspect-video flex items-center justify-center bg-black group/video cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {tweet.media[0].thumbnailUrl && (
                                <Image
                                    src={tweet.media[0].thumbnailUrl}
                                    alt="Video thumbnail"
                                    fill
                                    className="object-cover opacity-60 group-hover/video:opacity-80 transition-opacity"
                                />
                            )}
                            <div className="absolute inset-0 bg-black/20 group-hover/video:bg-black/10 transition-colors" />
                            <div className="relative z-10 flex flex-col items-center gap-2">
                                <PlayCircle className="w-16 h-16 text-white group-hover/video:scale-110 transition-transform" />
                                <span className="text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
                                    Watch on X
                                </span>
                            </div>
                        </a>
                    )}
                    {tweet.media.length > 1 && (
                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                            +{tweet.media.length - 1}
                        </div>
                    )}
                </div>
            )}

            {/* Footer Row */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50 dark:border-neutral-800/50 gap-2">
                {/* Metrics - Click to engage on Twitter */}
                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                    <a 
                        href={replyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 hover:text-blue-500 transition-colors"
                        title="Reply on X"
                    >
                        <MessageCircle className="w-3.5 h-3.5" />
                        <span>{formatNumber(tweet.metrics.replies)}</span>
                    </a>
                    <a 
                        href={retweetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 hover:text-green-500 transition-colors"
                        title="Retweet on X"
                    >
                        <Repeat2 className="w-3.5 h-3.5" />
                        <span>{formatNumber(tweet.metrics.retweets)}</span>
                    </a>
                    <a 
                        href={likeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 hover:text-red-500 transition-colors"
                        title="Like on X"
                    >
                        <Heart className="w-3.5 h-3.5" />
                        <span>{formatNumber(tweet.metrics.likes)}</span>
                    </a>
                </div>

                {/* View on X Link */}
                <a
                    href={tweet.tweetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors whitespace-nowrap flex-shrink-0"
                    title="View on X"
                >
                    <span className="hidden md:inline">View on X</span>
                    <span className="md:hidden">X</span>
                    <ExternalLink className="w-3 h-3" />
                </a>
            </div>
        </div>
    );
}
